import { BackpackIcon, BoxIcon, GlobeIcon, LayoutGridIcon, PackageIcon, ShoppingCartIcon } from 'lucide-vue-next'
import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

export const GearRouteName = {
  Containers: 'gear-containers',
  AllItems: 'gear-all-items',
  ShoppingPlanning: 'gear-shopping-planning',
  ContainerNew: 'gear-container-new',
  ContainerDetail: 'gear-container-detail',
  ContainerEdit: 'gear-container-edit',
  ItemNew: 'gear-item-new',
  ItemDetail: 'gear-item-detail',
  ItemEdit: 'gear-item-edit',
  PublicContainers: 'gear-public-containers',
  PublicContainerDetail: 'gear-public-container-detail',
  SharedContainerDetail: 'gear-shared-container-detail',
  PublicItemDetail: 'gear-public-item-detail',
  ContainerShareTokens: 'gear-container-share-tokens',
  Visualization: 'gear-visualization',
  Settings: 'gear-settings',
  CatalogueBrowser: 'gear-catalogue-browser',
  CatalogueManage: 'gear-catalogue-manage',
  CatalogueItemDetail: 'gear-catalogue-item-detail',
  CatalogueItemNew: 'gear-catalogue-item-new',
  CatalogueItemEdit: 'gear-catalogue-item-edit',
}

export const GearRoutePath = {
  Containers: '/gear',
  Import: '/gear?import=true',
  AllItems: '/gear/items',
  ShoppingPlanning: '/gear/shopping',
  ContainerNew: '/gear/new',
  ContainerDetail: '/gear/:id',
  ContainerDetailById: (id: string) => `/gear/${id}`,
  ContainerEdit: '/gear/:id/edit',
  ContainerEditById: (id: string) => `/gear/${id}/edit`,
  ItemNew: '/gear/:containerId/items/new',
  ItemDetail: '/gear/:containerId/items/:itemId',
  ItemDetailById: (containerId: string, itemId: string) => `/gear/${containerId}/items/${itemId}`,
  ItemEdit: '/gear/:containerId/items/:itemId/edit',
  ItemEditById: (containerId: string, itemId: string) => `/gear/${containerId}/items/${itemId}/edit`,
  PublicContainers: '/gear/public',
  PublicContainerDetail: '/gear/public/:id',
  PublicContainerDetailById: (id: string) => `/gear/public/${id}`,
  PublicItemDetail: '/gear/public/:containerId/items/:itemId',
  PublicItemDetailById: (containerId: string, itemId: string) => `/gear/public/${containerId}/items/${itemId}`,
  SharedContainerDetail: '/shared/container/:token',
  SharedContainerDetailByToken: (token: string) => `/shared/container/${token}`,
  ContainerShareTokens: '/gear/:id/share-tokens',
  ContainerShareTokensById: (id: string) => `/gear/${id}/share-tokens`,
  Visualization: '/gear/visualization',
  Settings: '/gear/settings',
  CatalogueBrowser: '/gear/catalogue',
  CatalogueManage: '/gear/catalogue/manage',
  CatalogueItemDetail: '/gear/catalogue/:id',
  CatalogueItemDetailById: (id: string) => `/gear/catalogue/${id}`,
  CatalogueItemNew: '/gear/catalogue/new',
  CatalogueItemEdit: '/gear/catalogue/:id/edit',
  CatalogueItemEditById: (id: string) => `/gear/catalogue/${id}/edit`,
}

export const GearRouteIcon: Partial<Record<keyof typeof GearRouteName, Component>> = {
  Containers: BackpackIcon,
  AllItems: PackageIcon,
  ShoppingPlanning: ShoppingCartIcon,
  Visualization: LayoutGridIcon,
  ContainerDetail: BoxIcon,
  PublicContainers: GlobeIcon,
}

export const gearRoutes: RouteRecordRaw[] = [
  {
    path: GearRoutePath.Containers,
    name: GearRouteName.Containers,
    component: () => import('@/modules/gear/pages/ContainersListPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.page.title' },
  },
  {
    path: GearRoutePath.AllItems,
    name: GearRouteName.AllItems,
    component: () => import('@/modules/gear/pages/AllItemsPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.allItems' },
  },
  {
    path: GearRoutePath.ShoppingPlanning,
    name: GearRouteName.ShoppingPlanning,
    component: () => import('@/modules/gear/pages/ShoppingPlanningPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.shoppingPlanning' },
  },
  {
    path: GearRoutePath.ContainerNew,
    name: GearRouteName.ContainerNew,
    component: () => import('@/modules/gear/pages/ContainerFormPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.container.create.title' },
  },
  {
    path: GearRoutePath.Visualization,
    name: GearRouteName.Visualization,
    component: () => import('@/modules/gear/pages/ContainerVisualizationPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.visualization.title' },
  },
  {
    path: GearRoutePath.ContainerDetail,
    name: GearRouteName.ContainerDetail,
    component: () => import('@/modules/gear/pages/ContainerDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.containerDetail' },
  },
  {
    path: GearRoutePath.ContainerEdit,
    name: GearRouteName.ContainerEdit,
    component: () => import('@/modules/gear/pages/ContainerFormPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.container.edit.title' },
  },
  {
    path: GearRoutePath.ItemNew,
    name: GearRouteName.ItemNew,
    component: () => import('@/modules/gear/pages/ItemFormPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.item.create' },
  },
  {
    path: GearRoutePath.ItemDetail,
    name: GearRouteName.ItemDetail,
    component: () => import('@/modules/gear/pages/ItemDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.itemDetail' },
  },
  {
    path: GearRoutePath.ItemEdit,
    name: GearRouteName.ItemEdit,
    component: () => import('@/modules/gear/pages/ItemFormPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.item.edit' },
  },
  {
    path: GearRoutePath.PublicContainers,
    name: GearRouteName.PublicContainers,
    component: () => import('@/modules/gear/pages/PublicContainersBrowserPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.publicContainers' },
  },
  {
    path: GearRoutePath.PublicContainerDetail,
    name: GearRouteName.PublicContainerDetail,
    component: () => import('@/modules/gear/pages/PublicContainerDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.publicContainerDetail' },
  },
  {
    path: GearRoutePath.PublicItemDetail,
    name: GearRouteName.PublicItemDetail,
    component: () => import('@/modules/gear/pages/PublicItemDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.publicItemDetail' },
  },
  {
    path: GearRoutePath.SharedContainerDetail,
    name: GearRouteName.SharedContainerDetail,
    component: () => import('@/modules/gear/pages/SharedContainerDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.pages.sharedContainerDetail' },
  },
  {
    path: GearRoutePath.ContainerShareTokens,
    name: GearRouteName.ContainerShareTokens,
    component: () => import('@/modules/gear/pages/ContainerShareTokensPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.shareTokens.title' },
  },
  {
    path: GearRoutePath.Settings,
    name: GearRouteName.Settings,
    component: () => import('@/pages/gear/GearSettingsPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.settings.page.title' },
  },
  {
    path: GearRoutePath.CatalogueBrowser,
    name: GearRouteName.CatalogueBrowser,
    component: () => import('@/modules/gear/pages/catalogue/CatalogueBrowserPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.catalogue.title' },
  },
  {
    path: GearRoutePath.CatalogueManage,
    name: GearRouteName.CatalogueManage,
    component: () => import('@/modules/gear/pages/catalogue/CatalogueManagePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, requiresAdmin: true, title: 'gear.catalogue.title' },
  },
  {
    path: GearRoutePath.CatalogueItemNew,
    name: GearRouteName.CatalogueItemNew,
    component: () => import('@/modules/gear/pages/catalogue/CatalogueItemFormPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, requiresAdmin: true, title: 'gear.catalogue.create.title' },
  },
  {
    path: GearRoutePath.CatalogueItemDetail,
    name: GearRouteName.CatalogueItemDetail,
    component: () => import('@/modules/gear/pages/catalogue/CatalogueItemDetailPage.vue'),
    meta: { layout: 'authenticated', title: 'gear.catalogue.itemDetail' },
  },
  {
    path: GearRoutePath.CatalogueItemEdit,
    name: GearRouteName.CatalogueItemEdit,
    component: () => import('@/modules/gear/pages/catalogue/CatalogueItemFormPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, requiresAdmin: true, title: 'gear.catalogue.edit.title' },
  },
]

